/**
 * `MessageBubble` — one message row (ARCHITECTURE §7). Visitor messages align
 * right in the brand color; agent/bot/system messages align left on a neutral
 * surface. Honors the org's customization: per-side bubble text color and
 * message font size (via `--lc-*` vars), plus agent/visitor avatars from the
 * shared preset library (or a custom uploaded image).
 */

import type { Message, OrgTheme } from '@flozic/shared/types';
import { avatarIcon, presetSvg } from '@flozic/shared/theme';
import { RichText } from './RichText';
import { MediaAttachments } from './MediaAttachments';

export interface MessageBubbleProps {
  message: Message;
  theme?: OrgTheme;
}

/** Round avatar for a bubble row — preset glyph or custom image; null when hidden. */
function Avatar({ side, theme }: { side: 'agent' | 'user'; theme?: OrgTheme }) {
  const iconId = side === 'agent' ? theme?.agentIcon : theme?.userIcon;
  if (side === 'user' && iconId === 'none') return null;
  const customUrl = side === 'agent' ? theme?.agentIconUrl : theme?.userIconUrl;
  const bgVar = side === 'agent' ? 'var(--lc-agent-avatar-bg)' : 'var(--lc-user-avatar-bg)';
  const fg = side === 'agent' ? 'var(--lc-on-primary)' : 'var(--lc-text)';
  return (
    <span
      className="flex h-6 w-6 shrink-0 items-center justify-center self-end overflow-hidden rounded-full"
      style={{ background: bgVar, color: fg }}
      aria-hidden="true"
    >
      {iconId === 'custom' && customUrl ? (
        <img src={customUrl} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
      ) : (
        <span dangerouslySetInnerHTML={{ __html: presetSvg(avatarIcon(iconId, side), 16) }} />
      )}
    </span>
  );
}

export function MessageBubble({ message, theme }: MessageBubbleProps) {
  const mine = message.senderType === 'visitor';
  const isSystem = message.senderType === 'system';
  const isBot = message.senderType === 'bot';

  if (isSystem) {
    return <div className="my-2 text-center text-xs text-lc-text/60">{message.body}</div>;
  }

  const bubble = (
    <div
      className={[
        // WhatsApp bubbles: visitor's own = light green (right), agent = white (left),
        // with the flattened corner on the sender's side for the tail feel.
        'max-w-[80%] px-3 py-2 leading-snug rounded-2xl shadow-sm',
        mine ? 'bg-lc-bubble-out rounded-br-sm' : 'bg-lc-bubble-in rounded-bl-sm',
      ].join(' ')}
      style={{
        color: mine ? 'var(--lc-bubble-out-text)' : 'var(--lc-bubble-in-text)',
        fontSize: 'var(--lc-msg-font-size)',
      }}
    >
      {isBot ? (
        <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wide text-lc-primary/80">
          Assistant
        </span>
      ) : null}
      {message.body ? (
        <span className="whitespace-pre-wrap break-words">
          <RichText body={message.body} />
        </span>
      ) : null}
      <MediaAttachments media={message.media} />
    </div>
  );

  return (
    <div className={`flex w-full items-end gap-1.5 ${mine ? 'justify-end' : 'justify-start'}`}>
      {!mine ? <Avatar side="agent" theme={theme} /> : null}
      {bubble}
      {mine ? <Avatar side="user" theme={theme} /> : null}
    </div>
  );
}
